import type { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import DotsVerticalIcon from "@redotech/redo-icons/dots-vertical.svg";
import { checkActionExecution } from "@redotech/redo-web/actions/action-definition";
import {
  conversationActionIds,
  type ConversationActionId,
} from "@redotech/redo-web/actions/conversation-actions/action-ids";
import {
  priorityScope,
  snoozeScope,
} from "@redotech/redo-web/actions/conversation-actions/scope-trees";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@redotech/redo-web/components/ui/dropdown-menu";
import { formatShortcut } from "@redotech/redo-web/keyboard-shortcuts/keyboard-shortcut";
import { useKeyboardShortcuts } from "@redotech/redo-web/keyboard-shortcuts/use-keyboard-shortcuts";
import { memo, useCallback, useMemo, useState } from "react";
import { getAction } from "../../../actions/action-registry";
import { ScopeMenu } from "../../../actions/scope-menu";
import { useConversationActionHandlers } from "../../../actions/use-conversation-actions";
import { useConversationsTableContext } from "../../conversations-table-context";

const STATUS_ACTION_IDS = [
  conversationActionIds.close,
  conversationActionIds.reopen,
  conversationActionIds.archive,
] as const;

const READ_ACTION_IDS = [
  conversationActionIds.markRead,
  conversationActionIds.markUnread,
] as const;

const DANGER_ACTION_IDS = [conversationActionIds.delete] as const;

interface ConversationActionsMenuProps {
  conversation: ConversationListItemResponse;
}

export const ConversationActionsMenu = memo(function ConversationActionsMenu({
  conversation,
}: ConversationActionsMenuProps) {
  const { actionMenuOpenForId, setActionMenuOpenForId } =
    useConversationsTableContext();

  const isOpen = actionMenuOpenForId === conversation.id;
  const setIsOpen = useCallback(
    (open: boolean) => setActionMenuOpenForId(open ? conversation.id : null),
    [setActionMenuOpenForId, conversation.id],
  );
  const [openSubmenuId, setOpenSubmenuId] = useState<string | null>(null);
  const handlers = useConversationActionHandlers(conversation.id);

  const bindings = useMemo(() => {
    if (!conversation.id || !conversation) {
      return [];
    }

    const simpleActionIds = [
      ...STATUS_ACTION_IDS,
      ...READ_ACTION_IDS,
      ...DANGER_ACTION_IDS,
    ];

    const actionBindings = simpleActionIds
      .map((id) => {
        const action = getAction(id);
        if (!action.shortcut) {
          return null;
        }
        return {
          shortcut: action.shortcut,
          handler: () => {
            const check = checkActionExecution(action, conversation);
            if (!check.canExecute) {
              return;
            }
            handlers[id]?.();
            // Close the actions dropdown menu after executing an action
            setIsOpen(false);
          },
        };
      })
      .filter(
        (binding): binding is NonNullable<typeof binding> => binding !== null,
      );

    const setPriorityAction = getAction(conversationActionIds.setPriority);
    const setSnoozeAction = getAction(conversationActionIds.snooze);
    const submenuTriggerBindings = [
      setPriorityAction.shortcut
        ? {
            shortcut: setPriorityAction.shortcut,
            handler: () => setOpenSubmenuId("priority"),
          }
        : null,
      setSnoozeAction.shortcut
        ? {
            shortcut: setSnoozeAction.shortcut,
            handler: () => setOpenSubmenuId("snooze"),
          }
        : null,
    ].filter(
      (binding): binding is NonNullable<typeof binding> => binding !== null,
    );

    return [...actionBindings, ...submenuTriggerBindings];
  }, [conversation, setIsOpen, handlers]);

  useKeyboardShortcuts({
    bindings,
    scope: "actionMenu",
    enabled: isOpen && openSubmenuId === null,
  });

  const handleCloseMenu = useCallback(() => setIsOpen(false), [setIsOpen]);

  const renderActionItem = (
    actionId: ConversationActionId,
    className?: string,
  ) => {
    const action = getAction(actionId);
    const check = checkActionExecution(action, conversation);
    if (action.isVisible && !action.isVisible(conversation)) {
      return null;
    }
    return (
      <DropdownMenuItem
        className={className}
        disabled={!check.canExecute}
        key={actionId}
        onClick={() => {
          handlers[actionId]?.();
          handleCloseMenu();
        }}
      >
        <div className="flex w-full items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            {action.Icon && <action.Icon className="h-4 w-4" />}
            <span>{action.title}</span>
          </div>
          {action.shortcut && (
            <span className="text-xs text-gray-400">
              {formatShortcut(action.shortcut)}
            </span>
          )}
        </div>
      </DropdownMenuItem>
    );
  };

  const setPriorityAction = getAction(conversationActionIds.setPriority);
  const isPriorityVisible =
    setPriorityAction.isVisible?.(conversation) !== false;
  const isPriorityEnabled = checkActionExecution(
    setPriorityAction,
    conversation,
  ).canExecute;

  const setSnoozeAction = getAction(conversationActionIds.snooze);
  const isSnoozeVisible = setSnoozeAction.isVisible?.(conversation) !== false;
  const isSnoozeEnabled = checkActionExecution(
    setSnoozeAction,
    conversation,
  ).canExecute;

  return (
    <DropdownMenu onOpenChange={setIsOpen} open={isOpen}>
      <DropdownMenuTrigger
        className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-100"
        onClick={(e) => e.stopPropagation()}
      >
        <DotsVerticalIcon className="h-4 w-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        {STATUS_ACTION_IDS.map((id) => renderActionItem(id))}

        <DropdownMenuSeparator />

        {READ_ACTION_IDS.map((id) => renderActionItem(id))}

        <DropdownMenuSeparator />

        {isPriorityVisible && (
          <DropdownMenuSub
            onOpenChange={(open) => setOpenSubmenuId(open ? "priority" : null)}
            open={openSubmenuId === "priority"}
          >
            <DropdownMenuSubTrigger disabled={!isPriorityEnabled}>
              <div className="flex w-full items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  {setPriorityAction.Icon && (
                    <setPriorityAction.Icon className="h-4 w-4" />
                  )}
                  <span>{setPriorityAction.title}</span>
                </div>
                {setPriorityAction.shortcut && (
                  <span className="text-xs text-gray-400">
                    {formatShortcut(setPriorityAction.shortcut)}
                  </span>
                )}
              </div>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <ScopeMenu
                conversation={conversation}
                depth={1}
                handlers={handlers}
                onClose={handleCloseMenu}
                scope={priorityScope}
              />
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}

        {isSnoozeVisible && (
          <DropdownMenuSub
            onOpenChange={(open) => setOpenSubmenuId(open ? "snooze" : null)}
            open={openSubmenuId === "snooze"}
          >
            <DropdownMenuSubTrigger disabled={!isSnoozeEnabled}>
              <div className="flex w-full items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  {setSnoozeAction.Icon && (
                    <setSnoozeAction.Icon className="h-4 w-4" />
                  )}
                  <span>{setSnoozeAction.title}</span>
                </div>
                {setSnoozeAction.shortcut && (
                  <span className="text-xs text-gray-400">
                    {formatShortcut(setSnoozeAction.shortcut)}
                  </span>
                )}
              </div>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <ScopeMenu
                conversation={conversation}
                depth={1}
                handlers={handlers}
                onClose={handleCloseMenu}
                scope={snoozeScope}
              />
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}

        {/* TODO: Add assign and addTag submenus when their option actions are implemented */}

        <DropdownMenuSeparator />

        {DANGER_ACTION_IDS.map((id) =>
          renderActionItem(id, "text-red-600 focus:text-red-600"),
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
});
